import { JSNS, type JsonObject } from '@zimbra-webif/types';
import type {
  Contact,
  ContactActionOp,
  ContactActionResult,
  ContactAttrs,
  ContactInfo,
  ContactSpec,
  ImportContactsResult,
  ModifyContactSpec,
  NewContactAttr,
  NewContactGroupMember,
  RestoreResolve,
} from '../../../types/src/contacts-entities';
import type { ZimbraClient } from '../client';

/** Sort orders accepted by GetContacts (`sortBy`). */
export type ContactSortBy = 'nameAsc' | 'nameDesc' | string;

/** Options for {@link ContactsApi.get}. */
export interface GetContactsOptions {
  /** specific contact ids to fetch (omit to fetch a whole folder / mailbox) */
  ids?: string[];
  /** restrict to this address-book folder */
  folderId?: string;
  /** sort order */
  sortBy?: ContactSortBy;
  /** include change-tracking fields (md/ms/rev) for sync */
  sync?: boolean;
  /** resolve contact-group members to full contacts */
  derefGroupMember?: boolean;
  /** include `memberOf` (groups each contact belongs to) */
  includeMemberOf?: boolean;
  /** include hidden attrs */
  returnHiddenAttrs?: boolean;
  /** include S/MIME cert info */
  returnCertInfo?: boolean;
  /** include `imapUid` */
  wantImapUid?: boolean;
  /** cap members returned per group (sets `tooManyMembers`) */
  maxMembers?: number;
  /** restrict the returned attr set */
  attributes?: string[];
  /** restrict attrs returned for dereferenced members */
  memberAttributes?: string[];
}

interface ContactListResponse {
  cn?: ContactInfo | ContactInfo[];
  [k: string]: unknown;
}

interface ImportContactsResponse {
  cn?: { n?: number; listOfCreatedIds?: string } | Array<{ n?: number; listOfCreatedIds?: string }>;
  [k: string]: unknown;
}

interface ExportContactsResponse {
  content?: { _content?: string } | Array<{ _content?: string }>;
  [k: string]: unknown;
}

interface GetContactBackupListResponse {
  backups?: { backup?: string | string[] };
  [k: string]: unknown;
}

interface ModifyProfileImageResponse {
  itemId?: number;
  [k: string]: unknown;
}

interface CsvFormatsResponse {
  csvFormats?: { name?: string } | Array<{ name?: string }>;
  [k: string]: unknown;
}

/** Normalize a Zimbra field that may be absent, a single object, or an array. */
function asArray<T>(v: T | T[] | undefined): T[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

/**
 * Fold a contact's attributes into `_attrs`. Zimbra's JSON serializer ALREADY
 * folds `<a n=..>` elements into a `_attrs` object on the `cn`, so we seed from
 * that; we also merge any explicit `a` array (XML-style) for completeness.
 */
export function toContact(ci: ContactInfo): Contact {
  const existing = (ci as { _attrs?: Record<string, string> })._attrs;
  const attrs: ContactAttrs & Record<string, string> = { ...(existing ?? {}) };
  for (const a of asArray(ci.a)) {
    if (a && typeof a.n === 'string' && a._content !== undefined) {
      attrs[a.n] = String(a._content);
    }
  }
  const out: Contact = { ...ci, _attrs: attrs };
  const members = asArray(ci.m);
  if (members.length > 0) out.members = members;
  return out;
}

/** Ranking-table maintenance (RankingAction). */
class RankingApi {
  constructor(private readonly client: ZimbraClient) {}

  /** RankingActionRequest — clear the entire autocomplete ranking table. */
  async reset(): Promise<void> {
    await this.client.call(JSNS.mail, 'RankingAction', { action: { op: 'reset' } });
  }

  /** RankingActionRequest — remove the ranking entry for one address. */
  async delete(email: string): Promise<void> {
    await this.client.call(JSNS.mail, 'RankingAction', { action: { op: 'delete', email } });
  }
}

/**
 * Contacts domain (urn:zimbraMail) — personal address books: contact + group
 * CRUD, bulk actions, import/export, server-side backup/restore, autocomplete
 * ranking, and the account profile image. (GetAvailableCsvFormats is account-ns
 * but lives here as it feeds import/export.)
 *
 * One public method per SOAP command, typed in and out, no `any` in signatures.
 */
export class ContactsApi {
  /** Autocomplete ranking-table maintenance (RankingAction). */
  readonly ranking: RankingApi;

  constructor(private readonly client: ZimbraClient) {
    this.ranking = new RankingApi(client);
  }

  /**
   * GetContactsRequest — fetch contacts by id, by folder, or the whole mailbox.
   * Returns full contacts with their fields folded into `_attrs`.
   */
  async get(opts: GetContactsOptions = {}): Promise<Contact[]> {
    const params: JsonObject = {};
    if (opts.folderId) params.l = opts.folderId;
    if (opts.sortBy) params.sortBy = opts.sortBy;
    if (opts.sync) params.sync = 1;
    if (opts.derefGroupMember) params.derefGroupMember = 1;
    if (opts.includeMemberOf) params.includeMemberOf = 1;
    if (opts.returnHiddenAttrs) params.returnHiddenAttrs = 1;
    if (opts.returnCertInfo) params.returnCertInfo = 1;
    if (opts.wantImapUid) params.wantImapUid = 1;
    if (opts.maxMembers !== undefined) params.maxMembers = opts.maxMembers;
    if (opts.attributes?.length) params.a = opts.attributes.map((n) => ({ n }));
    if (opts.memberAttributes?.length) params.ma = opts.memberAttributes.map((n) => ({ n }));
    if (opts.ids?.length) params.cn = opts.ids.map((id) => ({ id }));
    const res = await this.client.call<ContactListResponse>(JSNS.mail, 'GetContacts', params);
    return asArray(res.cn).map(toContact);
  }

  /** CreateContactRequest — create a personal contact or contact group. */
  async create(spec: ContactSpec, opts?: { verbose?: boolean; wantImapUid?: boolean; wantModSeq?: boolean }): Promise<Contact> {
    const params: JsonObject = { cn: this.buildContactSpec(spec) };
    if (opts?.verbose) params.verbose = 1;
    if (opts?.wantImapUid) params.wantImapUid = 1;
    if (opts?.wantModSeq) params.wantModSeq = 1;
    const res = await this.client.call<ContactListResponse>(JSNS.mail, 'CreateContact', params);
    return toContact(asArray(res.cn)[0] ?? {});
  }

  /**
   * Convenience: create a contact group with the given name and members.
   * Members are inline-address (`I`) refs unless given as `{ type, value }`.
   */
  async createGroup(
    name: string,
    members: Array<string | NewContactGroupMember> = [],
    opts?: { folderId?: string; verbose?: boolean },
  ): Promise<Contact> {
    const spec: ContactSpec = {
      attrs: [
        { n: 'nickname', _content: name },
        { n: 'fullName', _content: name },
        { n: 'type', _content: 'group' },
      ],
      members: members.map((m) =>
        typeof m === 'string' ? { type: 'I', value: m } : m,
      ),
    };
    if (opts?.folderId) spec.folderId = opts.folderId;
    return this.create(spec, opts?.verbose ? { verbose: true } : undefined);
  }

  /** ModifyContactRequest — update an existing contact or group. */
  async modify(
    id: number,
    changes: ModifyContactSpec,
    opts?: { replace?: boolean; verbose?: boolean; wantImapUid?: boolean; wantModSeq?: boolean },
  ): Promise<Contact> {
    const cn: JsonObject = { id };
    if (changes.tagNames !== undefined) cn.tn = changes.tagNames;
    if (changes.attrs?.length) {
      cn.a = changes.attrs.map((a) => {
        const { n, op, ...rest } = a;
        return op ? { n, op, ...rest } : { n, ...rest };
      });
    }
    if (changes.members?.length) {
      cn.m = changes.members.map((m) => {
        const { type, value, op } = m;
        return op ? { type, value, op } : { type, value };
      });
    }
    const params: JsonObject = { cn };
    if (opts?.replace) params.replace = 1;
    if (opts?.verbose) params.verbose = 1;
    if (opts?.wantImapUid) params.wantImapUid = 1;
    if (opts?.wantModSeq) params.wantModSeq = 1;
    const res = await this.client.call<ContactListResponse>(JSNS.mail, 'ModifyContact', params);
    return toContact(asArray(res.cn)[0] ?? {});
  }

  /**
   * ContactActionRequest — bulk op on contacts by id (move/trash/delete/tag/
   * flag/color/update). See {@link ContactActionOp}.
   */
  async action(
    ids: string | string[],
    op: ContactActionOp,
    opts: {
      folderId?: string;
      tagNames?: string;
      tag?: number;
      color?: number;
      rgb?: string;
      flags?: string;
      name?: string;
      tcon?: string;
      attrs?: NewContactAttr[];
    } = {},
  ): Promise<ContactActionResult> {
    const action: JsonObject = {
      id: Array.isArray(ids) ? ids.join(',') : ids,
      op,
    };
    if (opts.folderId) action.l = opts.folderId;
    if (opts.tagNames !== undefined) action.tn = opts.tagNames;
    if (opts.tag !== undefined) action.t = opts.tag;
    if (opts.color !== undefined) action.color = opts.color;
    if (opts.rgb !== undefined) action.rgb = opts.rgb;
    if (opts.flags !== undefined) action.f = opts.flags;
    if (opts.name !== undefined) action.name = opts.name;
    if (opts.tcon !== undefined) action.tcon = opts.tcon;
    if (opts.attrs?.length) action.a = opts.attrs.map((a) => ({ ...a }));
    const res = await this.client.call<{ action?: ContactActionResult }>(
      JSNS.mail,
      'ContactAction',
      { action },
    );
    return res.action ?? {};
  }

  /** Sugar: move contacts to a folder. */
  async move(ids: string | string[], folderId: string): Promise<ContactActionResult> {
    return this.action(ids, 'move', { folderId });
  }

  /** Sugar: move contacts to trash. */
  async trash(ids: string | string[]): Promise<ContactActionResult> {
    return this.action(ids, 'trash');
  }

  /** Sugar: hard-delete contacts. */
  async deleteContacts(ids: string | string[]): Promise<ContactActionResult> {
    return this.action(ids, 'delete');
  }

  /** Sugar: tag contacts by tag name (use `!tag` op to untag). */
  async tag(ids: string | string[], tagName: string, untag = false): Promise<ContactActionResult> {
    return this.action(ids, untag ? '!tag' : 'tag', { tagNames: tagName });
  }

  /** Sugar: flag contacts (pass untag/clear via `!flag`). */
  async flag(ids: string | string[], set = true): Promise<ContactActionResult> {
    return this.action(ids, set ? 'flag' : '!flag');
  }

  /**
   * ImportContactsRequest — import contacts from CSV/text into an address book.
   * Provide either inline `content.text` or an uploaded `content.aid`.
   */
  async import(opts: {
    folderId: string;
    content: { text?: string; aid?: string };
    contentType?: string;
    csvFormat?: string;
    csvLocale?: string;
  }): Promise<ImportContactsResult> {
    const params: JsonObject = {
      ct: opts.contentType ?? 'text/csv',
      l: opts.folderId,
    };
    if (opts.csvFormat !== undefined) params.csvfmt = opts.csvFormat;
    if (opts.csvLocale !== undefined) params.csvlocale = opts.csvLocale;
    const content: JsonObject = {};
    if (opts.content.aid !== undefined) content.aid = opts.content.aid;
    if (opts.content.text !== undefined) content._content = opts.content.text;
    params.content = content;
    const res = await this.client.call<ImportContactsResponse>(JSNS.mail, 'ImportContacts', params);
    const cn = asArray(res.cn)[0];
    const ids = cn?.listOfCreatedIds;
    return {
      numImported: cn?.n ?? 0,
      createdIds: ids ? ids.split(',').filter(Boolean) : [],
    };
  }

  /** ExportContactsRequest — export an address book (or all contacts) as text. */
  async export(opts: {
    contentType?: string;
    folderId?: string;
    csvFormat?: string;
    csvLocale?: string;
    csvDelimiter?: string;
  } = {}): Promise<string> {
    const params: JsonObject = { ct: opts.contentType ?? 'csv' };
    if (opts.folderId !== undefined) params.l = opts.folderId;
    if (opts.csvFormat !== undefined) params.csvfmt = opts.csvFormat;
    if (opts.csvLocale !== undefined) params.csvlocale = opts.csvLocale;
    if (opts.csvDelimiter !== undefined) params.csvsep = opts.csvDelimiter;
    const res = await this.client.call<ExportContactsResponse>(JSNS.mail, 'ExportContacts', params);
    return asArray(res.content)[0]?._content ?? '';
  }

  /** GetContactBackupListRequest — list server-side contact-backup snapshots. */
  async getBackupList(): Promise<string[]> {
    const res = await this.client.call<GetContactBackupListResponse>(
      JSNS.mail,
      'GetContactBackupList',
      {},
    );
    const b = res.backups?.backup;
    if (b === undefined) return [];
    return Array.isArray(b) ? b : [b];
  }

  /** RestoreContactsRequest — restore contacts from a named server-side backup. */
  async restore(backupFileName: string, resolve?: RestoreResolve): Promise<void> {
    const params: JsonObject = { contactsBackupFileName: backupFileName };
    if (resolve !== undefined) params.resolve = resolve;
    await this.client.call(JSNS.mail, 'RestoreContacts', params);
  }

  /**
   * ModifyProfileImageRequest — set the account avatar from an uploaded part
   * (`uploadId`) or inline base64 (`base64`). Returns the stored item id.
   */
  async setProfileImage(opts: { uploadId?: string; base64?: string }): Promise<{ itemId: number }> {
    const params: JsonObject = {};
    if (opts.uploadId !== undefined) params.uid = opts.uploadId;
    if (opts.base64 !== undefined) params.binaryB64Data = { _content: opts.base64 };
    const res = await this.client.call<ModifyProfileImageResponse>(
      JSNS.mail,
      'ModifyProfileImage',
      params,
    );
    return { itemId: res.itemId ?? 0 };
  }

  /**
   * GetAvailableCsvFormatsRequest (urn:zimbraAccount) — list named CSV formats
   * for the import/export dropdowns.
   */
  async csvFormats(): Promise<string[]> {
    const res = await this.client.call<CsvFormatsResponse>(
      JSNS.account,
      'GetAvailableCsvFormats',
      {},
    );
    return asArray(res.csvFormats)
      .map((f) => f?.name)
      .filter((n): n is string => typeof n === 'string');
  }

  /** Build the `<cn>` ContactSpec wire object for CreateContact. */
  private buildContactSpec(spec: ContactSpec): JsonObject {
    const cn: JsonObject = {};
    if (spec.id !== undefined) cn.id = spec.id;
    if (spec.folderId !== undefined) cn.l = spec.folderId;
    if (spec.tags !== undefined) cn.t = spec.tags;
    if (spec.tagNames !== undefined) cn.tn = spec.tagNames;
    if (spec.attrs?.length) cn.a = spec.attrs.map((a) => ({ ...a }));
    if (spec.members?.length) cn.m = spec.members.map((m) => ({ ...m }));
    return cn;
  }
}
